import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@goaltree/ui/components/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@goaltree/ui/components/popover'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation } from 'convex/react'
import { Info, MoreVertical, PencilLine, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { GoalTaskMenu } from '@/components/goal-task-menu'
import type { GoalTreeData } from '@/components/goal-tree'
import { InlineEditor, type InlineEditorHandle } from '@/components/inline-editor'
import { LinkifiedText } from '@/components/linkified-text'
import type { NodeDetail } from '@/components/node-detail-sheet'
import { NodeDetailSheet } from '@/components/node-detail-sheet'
import { UrgentFlag } from '@/components/task-meta'
import { ElapsedTitle, TimeMeterBar, useTaskTimeMeter } from '@/components/task-time-meter'
import { useDeleteNode } from '@/hooks/use-delete-node'
import { useRenameNode } from '@/hooks/use-rename-node'
import { haptic } from '@/lib/haptics'
import { playDoneSound } from '@/lib/sounds'
import { taskDescription, taskTitle } from '@/lib/tags'
import { taskTimeLabel } from '@/lib/task-time'
import { undoToast } from '@/lib/undo-toast'

type TaskStatus = 'backlog' | 'in-progress' | 'done'

type TaskNode = GoalTreeData['milestones'][number]['targets'][number]['tasks'][number]
type MilestoneNode = GoalTreeData['milestones'][number]

interface FlatTask extends TaskNode {
  targetTitle: string
  milestoneId: Id<'nodes'>
  kanbanStatus: TaskStatus
}

const COLUMN_IDS = new Set<string>(['backlog', 'in-progress', 'done'])

const DELETE_DROPPABLE_ID = 'delete'

type CardMenu = {
  isOwner: boolean
  viewerIsCoach: boolean
  onToggleDone: (task: FlatTask) => void
  onDelete: (task: FlatTask) => void
}

function SortableTaskCard({
  task,
  rename,
  onDetail,
  canEdit = true,
  menu,
}: {
  task: FlatTask
  rename: (id: Id<'nodes'>, t: string) => Promise<unknown>
  onDetail?: (task: FlatTask) => void
  canEdit?: boolean
  menu: CardMenu
}) {
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null)
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task._id,
    data: { type: 'task', status: task.kanbanStatus },
    disabled: task.kanbanStatus === 'done' || !canEdit,
  })

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      {...attributes}
      {...listeners}
      className={!canEdit || task.kanbanStatus === 'done' ? 'cursor-default' : 'cursor-pointer'}
      // Right-click only: on touch, long-press stays the drag gesture.
      onContextMenu={(e) => {
        if (!canEdit || window.matchMedia('(pointer: coarse)').matches) return
        e.preventDefault()
        setMenuPos({ x: e.clientX, y: e.clientY })
      }}
    >
      {menuPos && (
        <GoalTaskMenu
          pos={menuPos}
          onClose={() => setMenuPos(null)}
          task={task}
          isDone={task.kanbanStatus === 'done'}
          isInProgress={task.kanbanStatus === 'in-progress'}
          isOwner={menu.isOwner}
          viewerIsCoach={menu.viewerIsCoach}
          onToggleDone={() => menu.onToggleDone(task)}
          onDetails={() => onDetail?.(task)}
          onDelete={() => menu.onDelete(task)}
        />
      )}
      <TaskCard task={task} rename={canEdit ? rename : undefined} onDetail={onDetail} />
    </div>
  )
}

function TaskCard({
  task,
  onDetail,
}: {
  task: FlatTask
  rename?: (id: Id<'nodes'>, t: string) => Promise<unknown>
  onDetail?: (task: FlatTask) => void
}) {
  const { t } = useTranslation()
  const meter = useTaskTimeMeter({
    ...task,
    _creationTime: task.createdAt,
    status: task.kanbanStatus,
  })
  return (
    <div
      className="relative cursor-pointer rounded-xl border border-border/60 bg-card px-3 py-2.5 transition-colors hover:border-border"
      onClick={() => onDetail?.(task)}
    >
      {meter && <TimeMeterBar meter={meter} />}
      {/* Plain title (like the inbox kanban) so the whole card is draggable —
          an inline editor here would swallow the drag. Rename via the detail sheet. */}
      <div className="flex items-start gap-1.5">
        <p
          className={cn(
            'min-w-0 flex-1 text-[15px] font-semibold leading-snug [overflow-wrap:anywhere]',
            task.kanbanStatus === 'done'
              ? 'text-muted-foreground/60 line-through'
              : 'text-foreground',
          )}
        >
          <ElapsedTitle meter={meter}>{taskTitle(task)}</ElapsedTitle>
        </p>
        {task.effort === 'high' && task.kanbanStatus !== 'done' && (
          <UrgentFlag
            className="mt-[3px] [--dot-ring:var(--card)]"
            superUrgent={task.superUrgent}
            title={task.superUrgent ? undefined : t('filter.priorityUrgent')}
          />
        )}
      </div>
      {taskDescription(task) && (
        <p className="mt-0.5 line-clamp-2 text-[13px] text-muted-foreground [overflow-wrap:anywhere]">
          <LinkifiedText text={taskDescription(task)!} />
        </p>
      )}
      <div className="mt-1 flex items-center gap-2.5 overflow-hidden font-mono text-[11px] text-muted-foreground">
        <span className="min-w-0 truncate">{task.targetTitle}</span>
        <span className="ml-auto shrink-0">
          <span className="sm:hidden">{taskTimeLabel(task)}</span>
          <span className="hidden sm:inline">
            {taskTimeLabel(task)}・
            {t(`stats.effort${task.effort.charAt(0).toUpperCase() + task.effort.slice(1)}`)}
          </span>
        </span>
      </div>
    </div>
  )
}

function KanbanColumn({
  milestoneId,
  column,
  tasks,
  rename,
  onDetail,
  canEdit = true,
  isDragTarget = false,
  menu,
}: {
  milestoneId: Id<'nodes'>
  column: { id: TaskStatus; label: string }
  tasks: FlatTask[]
  rename: (id: Id<'nodes'>, t: string) => Promise<unknown>
  onDetail?: (task: FlatTask) => void
  canEdit?: boolean
  isDragTarget?: boolean
  menu: CardMenu
}) {
  const droppableId = `${milestoneId}:${column.id}`
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
    data: { type: 'column', status: column.id },
  })

  return (
    <div className="flex w-[410px] shrink-0 flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {column.label}
        </span>
        <span className="text-xs text-muted-foreground tabular-nums">{tasks.length}</span>
      </div>
      <SortableContext
        id={droppableId}
        items={tasks.map((t) => t._id)}
        strategy={verticalListSortingStrategy}
      >
        <div
          ref={setNodeRef}
          className={[
            'flex min-h-[60px] flex-col gap-1.5 rounded-lg border border-dashed p-2 transition-colors',
            // isOver fires only over empty space; isDragTarget keeps the column
            // (the dragged card's current column) highlighted while over its tasks.
            isOver || isDragTarget ? 'border-primary/50 bg-primary/5' : 'border-border/60',
          ].join(' ')}
        >
          {tasks.map((task) => (
            <SortableTaskCard
              key={task._id}
              task={task}
              rename={rename}
              onDetail={onDetail}
              canEdit={canEdit}
              menu={menu}
            />
          ))}
        </div>
      </SortableContext>
    </div>
  )
}

function DeleteZone() {
  const { t } = useTranslation()
  const { setNodeRef, isOver } = useDroppable({ id: DELETE_DROPPABLE_ID })
  return (
    <div className="flex w-[160px] shrink-0 flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-destructive/60">
          {t('common.delete')}
        </span>
      </div>
      <div
        ref={setNodeRef}
        className={cn(
          'flex min-h-[60px] flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed p-4 transition-colors',
          isOver
            ? 'border-destructive bg-destructive/10 text-destructive'
            : 'border-destructive/30 text-destructive/30',
        )}
      >
        <Trash2 className="h-5 w-5" />
        <span className="text-xs font-medium">{t('common.dropToDelete')}</span>
      </div>
    </div>
  )
}

function MilestoneKanban({
  milestone,
  goalId,
  onDetail,
  canEdit = true,
  viewerIsCoach = false,
  isOwner = false,
}: {
  milestone: MilestoneNode
  goalId: Id<'nodes'>
  onDetail?: (task: FlatTask) => void
  canEdit?: boolean
  viewerIsCoach?: boolean
  isOwner?: boolean
}) {
  const { t } = useTranslation()
  const columns: { id: TaskStatus; label: string }[] = [
    { id: 'backlog', label: t('goals.backlog') },
    { id: 'in-progress', label: t('goals.inProgress') },
    { id: 'done', label: t('goals.done') },
  ]
  const rename = useRenameNode()
  const deleteNode = useDeleteNode()
  const setTaskStatus = useMutation(api.nodes.setTaskStatus).withOptimisticUpdate(
    (localStore, { taskId, status }) => {
      const tree = localStore.getQuery(api.nodes.getGoalTree, { goalId })
      if (!tree) return
      localStore.setQuery(
        api.nodes.getGoalTree,
        { goalId },
        {
          ...tree,
          milestones: tree.milestones.map((ms) => ({
            ...ms,
            targets: ms.targets.map((tg) => ({
              ...tg,
              tasks: tg.tasks.map((t) =>
                t._id === taskId
                  ? { ...t, status, ...(status === 'done' ? { completedAt: Date.now() } : {}) }
                  : t,
              ),
            })),
          })),
        },
      )
    },
  )

  const flatTasks: FlatTask[] = milestone.targets.flatMap((tg) =>
    tg.tasks.map((t) => ({
      ...t,
      targetTitle: tg.title,
      milestoneId: milestone._id,
      kanbanStatus: t.completedAt !== null ? 'done' : (t.status as TaskStatus),
    })),
  )

  const [localTasks, setLocalTasks] = useState<FlatTask[]>(flatTasks)
  const [activeTask, setActiveTask] = useState<FlatTask | null>(null)
  const [pendingDelete, setPendingDelete] = useState<Id<'nodes'> | null>(null)

  useEffect(() => {
    document.body.classList.toggle('kanban-dragging', !!activeTask)
    return () => {
      document.body.classList.remove('kanban-dragging')
    }
  }, [activeTask])

  const flatKey = flatTasks.map((t) => `${t._id}:${t.kanbanStatus}`).join(',')
  const localKey = localTasks.map((t) => `${t._id}:${t.kanbanStatus}`).join(',')
  useEffect(() => {
    if (!activeTask && flatKey !== localKey) setLocalTasks(flatTasks)
  }, [flatKey, activeTask])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // "Marked done · Undo": undo puts the card back in the column it came from.
  const undoDone = (taskId: Id<'nodes'>, from: FlatTask['kanbanStatus']) =>
    undoToast(t('today.markedDone', { defaultValue: 'Marked done' }), () => {
      setTaskStatus({ taskId, status: from === 'in-progress' ? 'in-progress' : 'backlog' }).catch(
        () => toast.error("Couldn't update task — please try again."),
      )
    })

  const menu: CardMenu = {
    isOwner,
    viewerIsCoach,
    onToggleDone: (task) => {
      const done = task.kanbanStatus === 'done'
      if (!done) playDoneSound()
      setTaskStatus({ taskId: task._id, status: done ? 'backlog' : 'done' }).catch(() =>
        toast.error("Couldn't update task — please try again."),
      )
      if (!done) undoDone(task._id, task.kanbanStatus)
    },
    onDelete: (task) => setPendingDelete(task._id),
  }

  const byColumn = (status: TaskStatus) => localTasks.filter((t) => t.kanbanStatus === status)

  function resolveTargetStatus(over: DragOverEvent['over']): TaskStatus {
    if (!over) return 'backlog'
    const id = over.id as string
    // droppableId format is `milestoneId:status`
    if (id.includes(':')) {
      const suffix = id.split(':').pop()
      if (COLUMN_IDS.has(suffix!)) return suffix as TaskStatus
    }
    return localTasks.find((t) => t._id === id)?.kanbanStatus ?? 'backlog'
  }

  function handleDragStart({ active }: DragStartEvent) {
    haptic()
    setActiveTask(localTasks.find((t) => t._id === active.id) ?? null)
  }

  function handleDragOver({ active, over }: DragOverEvent) {
    if (!over || over.id === DELETE_DROPPABLE_ID) return
    const item = localTasks.find((t) => t._id === active.id)
    if (!item || item.kanbanStatus === 'done') return
    const target = resolveTargetStatus(over)
    if (item.kanbanStatus === target || (viewerIsCoach && target === 'done')) return
    setLocalTasks((prev) =>
      prev.map((t) => (t._id === active.id ? { ...t, kanbanStatus: target } : t)),
    )
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    setActiveTask(null)
    if (!over) return

    if (over.id === DELETE_DROPPABLE_ID) {
      setPendingDelete(active.id as Id<'nodes'>)
      return
    }

    const original = flatTasks.find((t) => t._id === active.id)
    if (!original || original.kanbanStatus === 'done') return
    const target = resolveTargetStatus(over)
    if (viewerIsCoach && target === 'done') return setLocalTasks(flatTasks)
    if (target !== original.kanbanStatus) {
      if (target === 'done') playDoneSound()
      setTaskStatus({ taskId: active.id as Id<'nodes'>, status: target }).catch(() => {
        toast.error("Couldn't update task — please try again.")
        setLocalTasks(flatTasks)
      })
      if (target === 'done') undoDone(active.id as Id<'nodes'>, original.kanbanStatus)
    }
  }

  return (
    <>
      <div className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-3 pl-3">
            {columns.map((col) => (
              <KanbanColumn
                key={col.id}
                milestoneId={milestone._id}
                column={col}
                tasks={byColumn(col.id)}
                rename={rename}
                onDetail={onDetail}
                canEdit={canEdit}
                menu={menu}
                isDragTarget={
                  activeTask
                    ? (localTasks.find((t) => t._id === activeTask._id)?.kanbanStatus ?? null) ===
                      col.id
                    : false
                }
              />
            ))}
            {canEdit && (
              <div
                className={cn(
                  'transition-all duration-200',
                  activeTask
                    ? 'opacity-100 translate-x-0'
                    : 'opacity-0 pointer-events-none -translate-x-2',
                )}
              >
                <DeleteZone />
              </div>
            )}
          </div>
          <DragOverlay>
            {activeTask && (
              <div className="rotate-1 opacity-90">
                <TaskCard task={activeTask} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.deleteTaskTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete
                ? t('common.deleteTaskDesc', {
                    title: localTasks.find((task) => task._id === pendingDelete)?.title ?? '',
                  })
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (!pendingDelete) return
                const snapshot = [...flatTasks]
                setLocalTasks((prev) => prev.filter((t) => t._id !== pendingDelete))
                deleteNode(pendingDelete).catch(() => {
                  toast.error("Couldn't delete task — please try again.")
                  setLocalTasks(snapshot)
                })
                setPendingDelete(null)
              }}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default function GoalKanban({
  tree,
  canEdit = true,
  openMilestoneId,
}: {
  tree: GoalTreeData
  canEdit?: boolean
  openMilestoneId?: string
}) {
  const { t } = useTranslation()
  const rename = useRenameNode()
  const deleteNode = useDeleteNode()
  const activeMilestone =
    tree.milestones.find((ms) => ms.completedAt === null && ms.progress < 100) ?? tree.milestones[0]
  const [openItems, setOpenItems] = useState<string[]>(activeMilestone ? [activeMilestone._id] : [])
  const [confirmDeleteMs, setConfirmDeleteMs] = useState<Id<'nodes'> | null>(null)
  const [detailNode, setDetailNode] = useState<NodeDetail | null>(null)
  const msTitleRefs = useRef<Map<string, InlineEditorHandle>>(new Map())
  const prevOpenMilestoneId = useRef<string | undefined>(null)
  useEffect(() => {
    if (!openMilestoneId || openMilestoneId === prevOpenMilestoneId.current) return
    prevOpenMilestoneId.current = openMilestoneId
    setOpenItems((prev) => (prev.includes(openMilestoneId) ? prev : [openMilestoneId]))
  }, [openMilestoneId])

  return (
    <div className="space-y-6">
      <Accordion value={openItems} onValueChange={setOpenItems} className="divide-y">
        {tree.milestones.map((ms) => {
          const msTasks = ms.targets.flatMap((tsk) => tsk.tasks)
          const msCompleted = msTasks.filter((tsk) => tsk.completedAt !== null).length
          const msCompletedTargets = ms.targets.filter(
            (tg) => tg.tasks.length > 0 && tg.tasks.every((tsk) => tsk.completedAt !== null),
          ).length
          const isMsDone = msTasks.length > 0 && msCompleted === msTasks.length
          return (
            <AccordionItem key={ms._id} value={ms._id} className="border-none py-1">
              <AccordionTrigger
                nativeButton={false}
                render={<div />}
                className="group/row flex w-full cursor-pointer items-center rounded-md py-3 pl-3 pr-2 text-sm font-semibold hover:no-underline hover:bg-muted/50 transition-colors [&_[data-slot=accordion-trigger-icon]]:hidden"
              >
                <InlineEditor
                  ref={(el) => {
                    if (el) msTitleRefs.current.set(ms._id, el)
                    else msTitleRefs.current.delete(ms._id)
                  }}
                  value={ms.title}
                  onSave={(title) => rename(ms._id, title)}
                  disabled={!canEdit}
                  className={cn(isMsDone && 'line-through text-muted-foreground')}
                />
                <div className="ml-auto flex shrink-0 items-center gap-2">
                  <span className="text-xs font-normal text-muted-foreground">
                    {t('goals.targetCount', {
                      completed: msCompletedTargets,
                      total: ms.targets.length,
                    })}{' '}
                    ・ {t('goals.taskCount', { completed: msCompleted, total: msTasks.length })}
                  </span>
                  {canEdit && (
                    <Popover>
                      <PopoverTrigger
                        className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover/row:opacity-100"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical className="h-3.5 w-3.5" />
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto min-w-[160px] !p-1 !gap-0 [&_button]:focus-visible:outline-none"
                        align="end"
                        side="bottom"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
                          onClick={() =>
                            setDetailNode({
                              nodeId: ms._id,
                              type: 'milestone',
                              title: ms.title,
                              category: tree.category,
                              goalTitle: tree.title,
                            })
                          }
                        >
                          <Info className="h-3.5 w-3.5" />
                          {t('common.details')}
                        </button>
                        {canEdit && (
                          <button
                            type="button"
                            className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
                            onClick={() => {
                              setTimeout(() => msTitleRefs.current.get(ms._id)?.startEdit(), 50)
                            }}
                          >
                            <PencilLine className="h-3.5 w-3.5" />
                            {t('common.rename')}
                          </button>
                        )}
                        <div className="my-1 h-px bg-border" />
                        <button
                          type="button"
                          className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
                          onClick={() => setConfirmDeleteMs(ms._id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {t('common.delete')}
                        </button>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-4">
                <MilestoneKanban
                  milestone={ms}
                  goalId={tree._id}
                  canEdit={canEdit}
                  viewerIsCoach={tree.viewerIsCoach}
                  isOwner={tree.isOwner}
                  onDetail={(task) =>
                    setDetailNode({
                      nodeId: task._id,
                      type: 'task',
                      title: task.title,
                      description: task.description,
                      category: tree.category,
                      goalTitle: tree.title,
                    })
                  }
                />
              </AccordionContent>
            </AccordionItem>
          )
        })}
      </Accordion>
      <NodeDetailSheet
        node={detailNode}
        goalId={tree._id}
        viewerIsCoach={tree.viewerIsCoach}
        onClose={() => setDetailNode(null)}
      />
      <AlertDialog
        open={!!confirmDeleteMs}
        onOpenChange={(open) => {
          if (!open) setConfirmDeleteMs(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('common.deleteNodeTitle', {
                label: tree.milestones.find((m) => m._id === confirmDeleteMs)?.title ?? '',
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('common.deleteNodeDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (confirmDeleteMs) deleteNode(confirmDeleteMs)
                setConfirmDeleteMs(null)
              }}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
